import { createSlice } from "@reduxjs/toolkit";
import {
  createCostSheet,
  fetchCostSheets,
  fetchOrganiserEvents,
  mapCostSheetActivity,
  GetActivityDetailsThunk,
  updateCostSheetStatus,
  submitEvent,
  submitTask,
  fetchChecklist,
  fetchOrgUsers,
  updateAssignedUsers,
  updateTaskStatus,
  getDocumentsDataThunk,
  GetUserRoleThunk,
  getDocumentsData,
} from "./eventOrganiserThunks";

const initialState = {
  Events: [],
  costSheets: [],
  loading: false,
  error: null,
  selectedEvent: null,
  selectedActivity: null,
  isNewCostSheet: false,
  costSheetData: null,
  mappingStatus: null,
  formData: {
    eventName: "",
    date: "",
    startTime: "",
    endTime: "",
    activities: [],
  },
  status: "idle",
  showForm: false,
  checklist: [],
  users: [],
  documentList: [],
  isOrgAccess: 3,
  roleData: JSON.parse(localStorage.getItem("roleData")) || [],
};

const eventOrganiserSlice = createSlice({
  name: "eventOrganiser",
  initialState,
  reducers: {
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
    },
    clearSelectedEvent: (state) => {
      state.selectedEvent = null;
    },
    clearSelectedActivity: (state) => {
      state.selectedActivity = null;
    },
    setSelectedActivity(state, action) {
      state.selectedActivity = action.payload;
    },
    setIsNewCostSheet: (state, action) => {
      state.isNewCostSheet = action.payload;
    },
    setCostSheet: (state, action) => {
      state.costSheets = action.payload;
    },
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    addActivity: (state) => {
      const newActivity = {
        name: "",
        description: "",
        headOfficerId: "",
        assigned_users: [],
        options: {
          isCostSheet: 1,
          isDocs: 1,
          isChecklist: 1,
          isQuery: 1,
          isConsent: 1,
          isSummary: 1,
          isUpload: 1,
        },
      };
      state.formData.activities.push(newActivity);
    },
    removeActivity: (state, action) => {
      state.formData.activities.splice(action.payload, 1);
    },
    updateActivity: (state, action) => {
      const { index, field, value } = action.payload;
      state.formData.activities[index][field] = value;
    },
    resetFormData: (state) => {
      state.formData = {
        eventName: "",
        date: "",
        startTime: "",
        endTime: "",
        activities: [],
      };
    },
    toggleForm: (state, action) => {
      state.showForm = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganiserEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganiserEvents.fulfilled, (state, action) => {
        state.Events = action.payload;
        state.loading = false;
      })
      .addCase(fetchOrganiserEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCostSheets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCostSheets.fulfilled, (state, action) => {
        state.loading = false;
        state.costSheets = action.payload;
      })
      .addCase(fetchCostSheets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCostSheet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCostSheet.fulfilled, (state, action) => {
        state.loading = false;
        state.costSheetData = action.payload;
      })
      .addCase(createCostSheet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create cost sheet";
      })
      .addCase(mapCostSheetActivity.pending, (state) => {
        state.loading = true;
      })
      .addCase(mapCostSheetActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.mappingStatus = action.payload;
      })
      .addCase(mapCostSheetActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to map cost sheet";
      })
      .addCase(GetActivityDetailsThunk.pending, (state) => {
        state.loading = false;
      })
      .addCase(GetActivityDetailsThunk.fulfilled, (state, action) => {
        state.loading = true;
        state.selectedActivity = action.payload;
      })
      .addCase(GetActivityDetailsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCostSheetStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCostSheetStatus.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateCostSheetStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
      .addCase(submitEvent.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(submitEvent.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(submitEvent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(submitTask.pending, (state) => {
        state.loader = true;
        state.error = null;
      })
      .addCase(submitTask.fulfilled, (state) => {
        state.loader = false;
        state.error = null;
      })
      .addCase(submitTask.rejected, (state, action) => {
        state.loader = false;
        state.error = action.payload || "Something went wrong";
      })
      .addCase(fetchChecklist.pending, (state) => {
        state.loader = true;
        state.error = null;
      })
      .addCase(fetchChecklist.fulfilled, (state, action) => {
        state.loader = false;
        state.checklist = action.payload;
      })
      .addCase(fetchChecklist.rejected, (state, action) => {
        state.loader = false;
        state.error = action.payload;
      })
      .addCase(fetchOrgUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrgUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchOrgUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAssignedUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAssignedUsers.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateAssignedUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update users";
      })
      .addCase(updateTaskStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateTaskStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update users";
      })
      .addCase(getDocumentsDataThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDocumentsDataThunk.fulfilled, (state, action) => {
        state.documentList = action.payload;
      })
      .addCase(getDocumentsDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update users";
      })
      .addCase(GetUserRoleThunk.pending, (state) => {
        state.loader = true;
        state.error = null;
      })
      .addCase(GetUserRoleThunk.fulfilled, (state, action) => {
        state.loader = false;
        state.roleData = action.payload;
        localStorage.setItem("roleData", JSON.stringify(action.payload));
      })
      .addCase(GetUserRoleThunk.rejected, (state, action) => {
        state.loader = false;
        state.error = action.payload;
      })
      .addCase(getDocumentsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDocumentsData.fulfilled, (state, action) => {
        state.loading = false;
        state.documentList = action.payload;
      })
      .addCase(getDocumentsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});
export const {
  setSelectedEvent,
  clearSelectedEvent,
  setSelectedActivity,
  setIsNewCostSheet,
  clearSelectedActivity,
  setCostSheet,
  updateFormData,
  addActivity,
  removeActivity,
  updateActivity,
  resetFormData,
  toggleForm,
} = eventOrganiserSlice.actions;
export default eventOrganiserSlice.reducer;
